import { Button, Form, Input, message, Space, Spin, Tabs } from "antd";
import { APP_PATH } from "constants/path";
import useBreadcrumb from "hooks/useBreadcrumb";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
	createSubscriptionPackageAPI,
	getSubscriptionPackageDetailsAPI,
	updateSubscriptionPackageAPI,
} from "service/apis/subscription-package";
import { translate } from "utils";
import { languages } from "constants/constants";
import useQuery from "hooks/useQuery";
import { t } from "@lingui/macro";
import UploadSingleImage from "components/upload-single-image/UploadSingleImage";
import { subscriptionPackageDefault } from "../redux/subscription-package.slice";
import PackagePrices from "./PackagePrices";
import Commissions from "./Commissions";
import Benefits from "./Benefits";
import OtherInformation from "./OtherInformation";
import CheckAccess from "hoc/CheckAccess";
import { LeftOutlined, SaveOutlined } from "@ant-design/icons";
import { PERMISSION } from "constants/permission";
import { SubscriptionPackage } from "../type";
import { CREATED_SUCCESS, UPDATED_SUCCESS } from "constants/messages";
import "./SubscriptionClassDetail.scss";

const SubscriptionClassDetail = () => {
	const { id } = useParams();
	const query = useQuery();
	const [isLoading, setIsLoading] = useState(false);
	const [form] = Form.useForm();
	// const lang = useSelector(selectLang);
	const { setBreadCrumbs } = useBreadcrumb();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [activeKey, setActiveKey] = useState("");

	const getData = async () => {
		setIsLoading(true);
		if (id) {
			const data = await getSubscriptionPackageDetailsAPI(id);
			if (data) {
				form.setFieldsValue(new SubscriptionPackage(data));
			}
		} else {
			form.setFieldsValue(new SubscriptionPackage(subscriptionPackageDefault));
		}

		setIsLoading(false);
	};

	useEffect(() => {
		setBreadCrumbs([
			{
				label: translate("Subscription package"),
				path: APP_PATH.subscriptionClass,
			},
			{
				label: translate("Subscription package detail"),
				path: `${APP_PATH.subscriptionClass}/${id}`,
			},
		]);
		getData();
		const tab = query.get("language");
		setActiveKey(tab || languages[0].key);
	}, []);
	const handleTabChanged = (key: string) => {
		navigate(`${pathname}?language=${key}`);
		setActiveKey(key);
	};
	const handleImageChanged = (url = "") => {
		form.setFieldValue("icon", url);
	};
	const handleImageBeforeUploaded = (message: string) => {
		if (message) {
			form.setFields([
				{
					name: "icon",
					errors: [message],
				},
			]);
		}
	};

	const handelSubmitted = async (payload: SubscriptionPackage) => {
		const data = SubscriptionPackage.serialize(payload);
		if (id) {
			setIsLoading(true);
			await updateSubscriptionPackageAPI(id, data);
			form.resetFields();
			await getData();
			setIsLoading(false);
			message.success(translate(UPDATED_SUCCESS));
			return;
		}
		setIsLoading(true);
		const result = await createSubscriptionPackageAPI(data);
		form.resetFields();
		result.id && navigate(APP_PATH.subscriptionClass);
		message.success(translate(CREATED_SUCCESS));

		setIsLoading(false);
	};

	const showTabErrored = () => {
		form
			.validateFields()
			.then()
			.catch((error) => {
				for (const err of error.errorFields) {
					navigate(`${pathname}?language=${err.name[1]}`);
					setActiveKey(err.name[1]);
					return;
				}
			});
	};
	return (
		<div className="h-full my-4 fade-in ">
			<Spin spinning={isLoading}>
				<Form
					form={form}
					name="register"
					onFinish={handelSubmitted}
					layout="inline"
					scrollToFirstError
				>
					<Space direction="vertical" size="middle" className="flex w-full">
						<div className="shadow-lg px-5 pt-5 pb-10 mb-10 bg-white p-5">
							<div className="mt-3 flex justify-between">
								<label className="font-semibold text-lg">{t`Main information`}</label>
							</div>
							<div className="flex">
								<div className="flex w-1/2">
									<div className="p-5">
										<p className="font-semibold mb-2">
											{translate("Image")}
											<span className="text-red-500">*</span>
										</p>
										<Form.Item shouldUpdate>
											{() => (
												<Form.Item
													name="icon"
													label={translate("Image")}
													rules={[{ required: true }]}
												>
													<UploadSingleImage
														onChange={handleImageChanged}
														beforeUpload={handleImageBeforeUploaded}
														imageUrl={form.getFieldValue("icon") || ""}
														folderUpload="news"
														className="w-20 subscription-upload-image"
													/>
												</Form.Item>
											)}
										</Form.Item>
									</div>
									<div className="p-5 flex-1 items-center flex">
										<p className="text-gray-400">
											{translate(
												"Recommended resolution is 48*48 with file size less than 2MB, keep visual elements centered",
											)}
										</p>
									</div>
								</div>
								<Tabs
									onChange={handleTabChanged}
									activeKey={activeKey}
									className="bg-white p-5 w-1/2"
									items={languages.map(lang => {
										return {
											label: translate(lang.nameE),
											key: lang.key,
											forceRender: true,
											children: (
												<>
													<div>
														<span>{translate("Subscription name")}</span>
														<span className="text-red-500">*</span>
														<Form.Item
															name={["name", lang.key]}
															label={translate("Subscription name")}
															rules={[
																{
																	required: true,
																},
															]}
															className="mt-2"
														>
															<Input
																placeholder={translate(
																	"Input subscription name",
																)}
															/>
														</Form.Item>
													</div>
													<div className="mt-5">
														<span>{translate("Description")}</span>
														<span className="text-red-500">*</span>
														<Form.Item
															name={["description", lang.key]}
															label={translate("Description")}
															rules={[
																{
																	required: true,
																},
															]}
															className="mt-2"
														>
															<Input.TextArea
																rows={4}
																placeholder={translate(
																	"Enter your description",
																)}
															/>
														</Form.Item>
													</div>
												</>
											),
										};
									})}
								/>
							</div>
						</div>

						{/* <MainInformation form={form} /> */}
						<PackagePrices form={form} />
						<Commissions form={form} />
						<Benefits form={form} />
						<OtherInformation form={form} />
						<div className="flex space-x-3 justify-end mt-3">
							<Button
								onClick={() => navigate(APP_PATH.subscriptionClass)}
								icon={<LeftOutlined />}
								type="primary"
								className="flex items-center bg-[#595959] hover:bg-[#c7c7c7] border-0"
							>
								{translate("Go back")}
							</Button>
							<CheckAccess perms={id ? [PERMISSION.SUBSCRIPTION.UPDATE] : [PERMISSION.SUBSCRIPTION.CREATE]}>
								<Button
									icon={<SaveOutlined />}
									type="primary"
									className="flex items-center bg-[#1890ff] ml-2"
									htmlType="submit"
									onClick={() => showTabErrored()}
								>
									{translate("Save")}
								</Button>
							</CheckAccess>
						</div>
					</Space>
				</Form>
			</Spin>
		</div>
	);
};
export default SubscriptionClassDetail;
